/**
 * Reusable place search input with debounced geocoding and dropdown.
 * Used for map search (fly to location) and store finder (set search center).
 * Supports both full place search (address, city, region) and city-only (store finder).
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { searchPlaces, DEFAULT_PLACE_TYPES, CITY_TYPES } from '../../services/geocodingService';
import './PlaceSearchInput.css';

const DEBOUNCE_MS = 280;
const MIN_QUERY_LENGTH = 2;

export function PlaceSearchInput({
  value,
  onChange,
  onSelect,
  placeholder = 'Search address or place…',
  types = DEFAULT_PLACE_TYPES,
  limit = 8,
  className = '',
  inputId,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}) {
  const [query, setQuery] = useState(value ?? '');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);
  /** When set, query was just set by selecting a suggestion; skip running search to avoid dropdown re-opening. */
  const selectedLabelRef = useRef(null);

  // Sync controlled value from parent; omit query to avoid overwriting user input when query changes
  useEffect(() => {
    if (value !== undefined && value !== query) setQuery(value);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const trimmed = (query || '').trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      setOpen(false);
      selectedLabelRef.current = null;
      return undefined;
    }
    if (selectedLabelRef.current !== null && trimmed === selectedLabelRef.current) {
      setSuggestions([]);
      setOpen(false);
      selectedLabelRef.current = null;
      return undefined;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      searchPlaces(trimmed, { limit, types, signal: controller.signal })
        .then((results) => {
          if (!controller.signal.aborted) {
            setSuggestions(results);
            setOpen(results.length > 0);
          }
        })
        .catch(() => {
          if (!controller.signal.aborted) setSuggestions([]);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [query, limit, types]);

  const handleChange = useCallback(
    (e) => {
      selectedLabelRef.current = null;
      const v = e.target.value;
      setQuery(v);
      if (onChange) onChange(v);
    },
    [onChange]
  );

  const handleSelect = useCallback(
    (place) => {
      const label = place?.label || place?.name || '';
      selectedLabelRef.current = String(label).trim();
      setQuery(label);
      setSuggestions([]);
      setOpen(false);
      // Parent onSelect owns the committed value. Do not also call onChange(label) —
      // controlled parents often clear the selected place when query "changes", which
      // races with onSelect and leaves the field empty.
      if (onSelect) onSelect(place);
    },
    [onSelect]
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (!open || suggestions.length === 0) return;
      if (e.key === 'Escape') {
        setOpen(false);
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const idx = suggestions.findIndex((s) => s.id === (e.target.dataset?.activeId || ''));
        let next = e.key === 'ArrowDown' ? idx + 1 : idx - 1;
        if (next < 0) next = suggestions.length - 1;
        if (next >= suggestions.length) next = 0;
        const el = containerRef.current?.querySelector(`[data-suggestion-id="${suggestions[next].id}"]`);
        if (el) {
          el.focus();
          e.target.dataset.activeId = suggestions[next].id;
        }
      }
    },
    [open, suggestions]
  );

  return (
    <div className={`place-search-input ${className}`.trim()} ref={containerRef}>
      <div className="place-search-input__wrap">
        <input
          type="text"
          id={inputId}
          className="place-search-input__field form-control"
          value={query}
          onChange={handleChange}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          aria-label={ariaLabel || placeholder}
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls="place-search-suggestions"
          data-testid={dataTestId}
        />
        {loading && (
          <span className="place-search-input__spinner" aria-hidden="true">
            …
          </span>
        )}
      </div>
      {open && suggestions.length > 0 && (
        <ul
          id="place-search-suggestions"
          className="place-search-input__suggestions"
          role="listbox"
        >
          {suggestions.map((place) => (
            <li key={place.id} role="option" aria-selected="false">
              <button
                type="button"
                className="place-search-input__suggestion"
                onMouseDown={(e) => {
                  // Keep input from blurring before click; otherwise the list can
                  // unmount and the select never commits.
                  e.preventDefault();
                }}
                onClick={() => handleSelect(place)}
                data-suggestion-id={place.id}
              >
                <span className="place-search-input__suggestion-main">{place.label || place.name}</span>
                {place.subtitle && (
                  <span className="place-search-input__suggestion-sub">{place.subtitle}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

PlaceSearchInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onSelect: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  types: PropTypes.string,
  limit: PropTypes.number,
  className: PropTypes.string,
  inputId: PropTypes.string,
  'aria-label': PropTypes.string,
  'data-testid': PropTypes.string,
};

export { DEFAULT_PLACE_TYPES, CITY_TYPES };
